import { ApiClient } from "../api-client";
import {
	ApiClientError,
	ApiClientResponseError,
	handleApiError,
	handleApiErrorWithSchema,
	UnauthenticatedError,
} from "../errors";
import { TypeFromSchemaValidator } from "../schema-validator";
import schemaValidators from "./schema-validators";

const emptyPaginatedResults = {
	currentPage: 0,
	totalElements: 0,
	totalPages: 1,
	results: [],
};

type AssessmentUuidInfo = {
	instituteUuid: string;
	courseUuid: string;
	assessmentUuid: string;
};

export default class LiveClient extends ApiClient<typeof schemaValidators> {
	constructor(config: ApiClient["config"]) {
		super(config, schemaValidators);
	}

	getLiveSessionsWithUsers = ({
		instituteUuid,
		courseUuid,
		assessmentUuid,
		sortBy,
		order,
		size,
		page = 0,
	}: {
		instituteUuid?: string;
		courseUuid?: string;
		assessmentUuid?: string;
		sortBy?: "name" | "startTime";
		order?: "asc" | "desc";
		size?: number;
		page?: number;
	}) => {
		const searchParams = new URLSearchParams();
		if (instituteUuid) {
			searchParams.set("instituteId", instituteUuid);
		}
		if (courseUuid) {
			searchParams.set("courseId", courseUuid);
		}
		if (assessmentUuid) {
			searchParams.set("assessmentId", assessmentUuid);
		}
		if (sortBy) {
			searchParams.set("sortBy", sortBy);
		}
		if (order) {
			searchParams.set("order", order);
		}
		if (size != null) {
			searchParams.set("size", String(size));
		}
		if (page != null) {
			searchParams.set("page", String(page));
		}
		searchParams.set("includeUsers", "true");
		return handleApiErrorWithSchema(
			super.fetch(new URL(`/api/live/liveSession?${searchParams}`, `${this.config.verityOrigin}`)),
			this.schemaValidators.liveSessionListWithUsers,
		);
	};

	getCourseLiveSessionInfo = async (instituteUuid: string, courseUuid: string) => {
		try {
			const resp = await super.fetch(
				new URL(
					`/api/live/liveSession?instituteId=${instituteUuid}&courseId=${courseUuid}`,
					`${this.config.verityOrigin}`,
				),
			);
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (this.schemaValidators.liveSessionList.validate(data)) {
					return data;
				}
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		console.error("Failed to retrieve course live session info");
		return emptyPaginatedResults;
	};

	getLiveSession = async (liveSessionId: string) => {
		try {
			const resp = await super.fetch(new URL(`/api/live/liveSession/${liveSessionId}`, `${this.config.verityOrigin}`));
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (this.schemaValidators.singleLiveSession.validate(data)) {
					return data;
				}
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		throw new ApiClientError();
	};

	getLiveSessionLiveState = async (liveSessionId: string) => {
		try {
			const resp = await super.fetch(
				new URL(`/api/live/liveSession/${liveSessionId}/liveState`, `${this.config.verityOrigin}`),
			);
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (this.schemaValidators.liveSessionLiveState.validate(data)) {
					return data;
				}
			} else {
				return undefined;
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		throw new ApiClientError();
	};

	getLiveSessionUser = async (liveSessionId: string, userId: string) => {
		try {
			const resp = await super.fetch(
				new URL(`/api/live/liveSession/${liveSessionId}/user/${userId}`, `${this.config.verityOrigin}`),
			);
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (this.schemaValidators.liveSessionUser.validate(data)) {
					return data;
				}
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		throw new ApiClientError();
	};

	getLiveSessionUserSession = async (liveSessionId: string, userSessionId: string) => {
		try {
			const resp = await super.fetch(
				new URL(`/api/live/liveSession/${liveSessionId}/userSession/${userSessionId}`, `${this.config.verityOrigin}`),
			);
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (this.schemaValidators.liveSessionUserSession.validate(data)) {
					return data;
				}
			} else if (resp.status === 404) {
				return null;
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		throw new ApiClientError();
	};

	getLiveSessionInvitations = async (liveSessionId: string) => {
		try {
			const resp = await super.fetch(
				new URL(`/api/live/liveSession/${liveSessionId}/invitation`, `${this.config.verityOrigin}`),
				{ credentials: "include" },
			);
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (this.schemaValidators.liveSessionInvitations.validate(data)) {
					return data;
				}
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		throw new ApiClientError();
	};

	getCourseLiveProctorInfo = async (lmsProvidedCourseId: string) => {
		try {
			const resp = await super.fetch(
				new URL(`/api/verity/user/instructor-roster/${lmsProvidedCourseId}`, `${this.config.verityOrigin}`),
				{ credentials: "include" },
			);
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (this.schemaValidators.courseLiveProctorInfo.validate(data)) {
					return data;
				}
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		console.error("Failed to retrieve course live proctor info");
		return [];
	};

	getLiveProctorGroup = async (proctorGroupId: number) =>
		handleApiErrorWithSchema(
			super.fetch(new URL(`/api/live/proctorGroup/${proctorGroupId}`, `${this.config.verityOrigin}`), {
				credentials: "include",
			}),
			this.schemaValidators.liveProctorGroup,
		);

	getUserProctorGroups = async (userId: string) =>
		handleApiErrorWithSchema(
			super.fetch(new URL(`/api/live/user/${userId}/proctorGroup`, `${this.config.verityOrigin}`), {
				credentials: "include",
			}),
			this.schemaValidators.liveProctorGroups,
		);

	getLiveInstituteProctorGroup = async (instituteUuid: string) => {
		try {
			const resp = await super.fetch(
				new URL(`/api/live/proctorGroup?instituteId=${instituteUuid}`, `${this.config.verityOrigin}`),
				{
					credentials: "include",
				},
			);
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (this.schemaValidators.instituteProctorGroups.validate(data)) {
					return {
						...data,
						results: data.results
							.filter(({ allowedInstitutes }) =>
								allowedInstitutes.some(({ instituteId }) => instituteId === instituteUuid),
							)
							.map((proctorGroup) => {
								const {
									minHoursNotice,
									trackCancellationHoursBefore,
									trackCancellationMessage,
									autoCancelEmptyHoursBefore,
								} = proctorGroup.allowedInstitutes.find(({ instituteId }) => instituteId === instituteUuid)!;
								return {
									...proctorGroup,
									minHoursNotice,
									trackCancellationHoursBefore,
									trackCancellationMessage,
									autoCancelEmptyHoursBefore,
								};
							}),
					};
				}
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		console.error("Failed to retrieve institute proctor groups");
		return emptyPaginatedResults;
	};

	getAssessmentLiveSessionInfo = async ({ instituteUuid, courseUuid, assessmentUuid }: AssessmentUuidInfo) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/live/liveSession?instituteId=${instituteUuid}&courseId=${courseUuid}&assessmentId=${assessmentUuid}`,
			);
			const liveSessionInfo: unknown = await response.json();
			if (this.schemaValidators.liveSessionList.validate(liveSessionInfo)) {
				return liveSessionInfo;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getLiveSessionNotes: (
		liveSessionId: string,
	) => Promise<TypeFromSchemaValidator<(typeof schemaValidators)["liveSessionNotes"]>> = async (liveSessionId) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/live/liveSession/${liveSessionId}/notes`);
			const liveSessionNotes: unknown = await response.json();
			if (this.schemaValidators.liveSessionNotes.validate(liveSessionNotes)) {
				return liveSessionNotes;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getUserCourseLiveSessions = async (userUuid: string, courseUuid: string) => {
		try {
			const liveSessions: unknown = await (
				await super.fetch(`${this.config.verityOrigin}/api/live/user/${userUuid}/liveSession?courseId=${courseUuid}`)
			).json();
			if (this.schemaValidators.liveSessionList.validate(liveSessions)) {
				return liveSessions.results;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getUserAssessmentLiveSessions = async (assessmentUuid: string) => {
		try {
			const liveSessions: unknown = await (
				await super.fetch(
					`${this.config.verityOrigin}/api/live/user/${
						(await this.getJwt())?.uid
					}/liveSession?assessmentId=${assessmentUuid}&sortBy=endTime`,
				)
			).json();
			if (this.schemaValidators.liveSessionList.validate(liveSessions)) {
				return liveSessions.results;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	doesLiveSessionExistForAssessment = async (assessmentUuidInfo: AssessmentUuidInfo): Promise<boolean> => {
		const result = await this.getAssessmentLiveSessionInfo(assessmentUuidInfo);
		return result.totalElements > 0;
	};

	getInstituteLiveSessions = async (instituteUuid: string) => {
		try {
			const liveSessions: unknown = await (
				await super.fetch(`${this.config.verityOrigin}/api/live/liveSession?instituteId=${instituteUuid}`)
			).json();
			if (this.schemaValidators.liveSessionList.validate(liveSessions)) {
				return liveSessions;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getCourseLiveSessions = async (instituteUuid: string, courseUuid: string) => {
		try {
			const liveSessions: unknown = await (
				await super.fetch(
					`${this.config.verityOrigin}/api/live/liveSession?instituteId=${instituteUuid}&courseId=${courseUuid}`,
				)
			).json();
			if (this.schemaValidators.liveSessionList.validate(liveSessions)) {
				return liveSessions;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getStudentInstituteId = async (): Promise<number> => {
		try {
			const instituteId: unknown = await (
				await super.fetch(`${this.config.verityOrigin}/api/verity/user/institute-id`)
			).json();
			if (typeof instituteId === "number") {
				return instituteId;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	addLiveSession = async (
		{ instituteUuid, courseUuid, assessmentUuid }: AssessmentUuidInfo,
		payload: {
			name: string;
			startTime: Date;
			endTime: Date;
			capacity: number;
			proctorGuide?: string;
			users?: { userId: string; role: "proctor" | "student" }[];
		},
	) => {
		try {
			const resp = await super.fetch(new URL(`/api/live/liveSession`, `${this.config.verityOrigin}`), {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					...payload,
					users: payload.users ?? [],
					instituteId: instituteUuid,
					courseId: courseUuid,
					assessmentId: assessmentUuid,
				}),
				credentials: "include",
			});
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (this.schemaValidators.singleLiveSession.validate(data)) {
					return {
						success: resp.ok,
						status: resp.status,
						error: undefined,
						updatedSession: data,
					};
				}
			} else {
				const errorMessage = await resp.text();
				return {
					success: resp.ok,
					status: resp.status,
					error: errorMessage.includes("html") ? undefined : JSON.parse(errorMessage),
					updatedSession: undefined,
				};
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		return {
			success: false,
			error: undefined,
			updatedSession: undefined,
		};
	};

	deleteLiveSession = async ({ liveSessionId }: { liveSessionId: string }) => {
		try {
			const resp = await super.fetch(new URL(`/api/live/liveSession/${liveSessionId}`, `${this.config.verityOrigin}`), {
				method: "DELETE",
				credentials: "include",
			});
			if (resp.ok) {
				return {
					success: resp.ok,
					status: resp.status,
					error: undefined,
				};
			} else {
				const errorMessage = await resp.text();
				return {
					success: resp.ok,
					status: resp.status,
					error: errorMessage.includes("html") ? undefined : JSON.parse(errorMessage),
					updatedSession: undefined,
				};
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		return {
			success: false,
			error: undefined,
		};
	};

	modifyLiveSession = async ({
		liveSessionId,
		payload,
	}: {
		liveSessionId: string;
		payload: {
			name?: string;
			startTime?: Date;
			endTime?: Date;
			capacity?: number;
			registrationStartTime?: Date | null;
			registrationEndTime?: Date | null;
		};
	}) => {
		try {
			const resp = await super.fetch(new URL(`/api/live/liveSession/${liveSessionId}`, `${this.config.verityOrigin}`), {
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
				credentials: "include",
			});
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (this.schemaValidators.singleLiveSession.validate(data)) {
					return {
						success: resp.ok,
						status: resp.status,
						error: undefined,
						updatedSession: data,
					};
				}
			} else {
				const errorMessage = await resp.text();
				return {
					success: resp.ok,
					status: resp.status,
					error: errorMessage.includes("<!DOCTYPE html>") ? undefined : JSON.parse(errorMessage),
					updatedSession: undefined,
				};
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		return {
			success: false,
			error: undefined,
			updatedSession: undefined,
		};
	};

	saveLiveSessionInstituteUser = async ({
		liveSessionId,
		action,
		role,
		userId,
	}: {
		liveSessionId: string;
		action: "register" | "deregister";
		role: "student" | "proctor";
		userId: string;
	}) => {
		try {
			let resp: Response;
			if (action === "register") {
				resp = await super.fetch(
					new URL(`/api/live/liveSession/${liveSessionId}/user`, `${this.config.verityOrigin}`),
					{
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ userId, role }),
						credentials: "include",
					},
				);
			} else {
				resp = await super.fetch(
					new URL(`/api/live/liveSession/${liveSessionId}/user/${userId}`, `${this.config.verityOrigin}`),
					{
						method: "DELETE",
						credentials: "include",
					},
				);
			}
			if (resp.ok) {
				if (action === "register") {
					const data: unknown = await resp.json();
					if (this.schemaValidators.registerLiveSessionInstituteUser.validate(data)) {
						return {
							success: resp.ok,
							error: undefined,
							updatedSession: data,
						};
					}
				} else if (action === "deregister") {
					return {
						success: resp.ok,
						error: undefined,
						updatedSession: undefined,
					};
				}
			} else {
				const errorMessage = await resp.text();
				return {
					success: resp.ok,
					error: errorMessage.includes("<!DOCTYPE html>") ? undefined : JSON.parse(errorMessage),
					updatedSession: undefined,
				};
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		return {
			success: false,
			error: undefined,
			updatedSession: undefined,
		};
	};

	addLiveSessionUser = ({
		liveSessionId,
		role,
		userId,
	}: {
		liveSessionId: string;
		role: "student" | "proctor";
		userId: string;
	}) => {
		return handleApiErrorWithSchema(
			super.fetch(new URL(`/api/live/liveSession/${liveSessionId}/user`, `${this.config.verityOrigin}`), {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ userId, role }),
				credentials: "include",
			}),
			this.schemaValidators.registerLiveSessionInstituteUser,
		);
	};

	removeLiveSessionUser = async (liveSessionId: string, userId: string) => {
		await handleApiError(
			super.fetch(new URL(`/api/live/liveSession/${liveSessionId}/user/${userId}`, `${this.config.verityOrigin}`), {
				method: "DELETE",
				credentials: "include",
			}),
		);
	};

	registerSelfAsProctor = async (liveSessionId: string) => {
		const userId = (await this.getJwt())?.uid;
		if (!userId) {
			throw new UnauthenticatedError();
		}
		try {
			await this.addLiveSessionUser({
				liveSessionId,
				userId,
				role: "proctor",
			});
		} catch (e) {
			if (!(e instanceof ApiClientResponseError) || e.code !== "userExists") {
				throw e;
			}
		}
	};

	saveLiveSessionProctorGroup = async ({
		liveSessionId,
		action,
		proctorGroupId,
	}: {
		liveSessionId: string;
		action: "registerGroup" | "deregisterGroup";
		proctorGroupId: number;
	}) => {
		try {
			let resp: Response;
			if (action === "registerGroup") {
				resp = await super.fetch(
					new URL(`/api/live/liveSession/${liveSessionId}/invitation/${proctorGroupId}`, `${this.config.verityOrigin}`),
					{
						method: "POST",
						credentials: "include",
					},
				);
			} else {
				resp = await super.fetch(
					new URL(`/api/live/liveSession/${liveSessionId}/invitation/${proctorGroupId}`, `${this.config.verityOrigin}`),
					{
						method: "DELETE",
						credentials: "include",
					},
				);
			}
			if (resp.ok) {
				if (action === "registerGroup") {
					const data: unknown = await resp.json();
					if (this.schemaValidators.registerLiveSessionProctorGroup.validate(data)) {
						return {
							success: resp.ok,
							error: undefined,
							updatedSession: data,
						};
					}
				} else if (action === "deregisterGroup") {
					return {
						success: resp.ok,
						error: undefined,
					};
				}
			} else {
				const errorMessage = await resp.text();
				return {
					success: resp.ok,
					error: errorMessage.includes("<!DOCTYPE html>") ? undefined : JSON.parse(errorMessage),
					updatedSession: undefined,
				};
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		return {
			success: false,
			error: undefined,
			updatedSession: undefined,
		};
	};

	createProctorGroupInvitation = (liveSessionId: string, proctorGroupId: number) => {
		return handleApiErrorWithSchema(
			super.fetch(
				new URL(`/api/live/liveSession/${liveSessionId}/invitation/${proctorGroupId}`, `${this.config.verityOrigin}`),
				{
					method: "POST",
					credentials: "include",
				},
			),
			this.schemaValidators.liveSessionInvitation,
		);
	};

	deleteProctorGroupInvitation = async (liveSessionId: string, proctorGroupId: number) => {
		await handleApiError(
			super.fetch(
				new URL(`/api/live/liveSession/${liveSessionId}/invitation/${proctorGroupId}`, `${this.config.verityOrigin}`),
				{
					method: "DELETE",
					credentials: "include",
				},
			),
		);
	};

	markLiveSessionViewed = async (liveSessionId: string) => {
		try {
			const resp = await super.fetch(
				new URL(`/api/live/liveSession/${liveSessionId}/view`, `${this.config.verityOrigin}`),
				{
					method: "POST",
					credentials: "include",
				},
			);
			if (resp.ok) {
				return true;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	endLiveSession = async (liveSessionId: string) => {
		try {
			const resp = await super.fetch(
				new URL(`/api/live/liveSession/${liveSessionId}/end`, `${this.config.verityOrigin}`),
				{
					method: "POST",
					credentials: "include",
				},
			);
			if (resp.ok) {
				return true;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};
}
